import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';
import {
  X, Inbox, Send, ArrowLeft, Trash2, Mail, Search, ChevronLeft, ChevronRight,
} from 'lucide-react';
import axios from 'axios';
import UserAvatar from './UserAvatar';
import { authSelector } from '../features/auth/slice';

function MessageModal({ open, onClose, initialMode = 'inbox', initialRecipient = null, onSent }) {
  const { distributorTheme } = useSelector(authSelector.selectAuthState);
  const isDark = distributorTheme === 'dark';

  const modalBg = isDark ? 'bg-[#111827]' : 'bg-white';
  const modalBorder = isDark ? 'border-white/10' : 'border-slate-200';
  const headerBorder = isDark ? 'border-white/[0.08]' : 'border-slate-100';
  const rowHover = isDark ? 'hover:bg-white/[0.04]' : 'hover:bg-slate-50';
  const unreadRowBg = isDark ? 'bg-sky-500/[0.06] hover:bg-sky-500/[0.1]' : 'bg-sky-50/50 hover:bg-sky-50';
  const textPrimary = isDark ? 'text-white' : 'text-slate-900';
  const textSecondary = isDark ? 'text-slate-300' : 'text-slate-600';
  const textMuted = isDark ? 'text-slate-400' : 'text-slate-500';
  const textFaint = isDark ? 'text-slate-500' : 'text-slate-400';
  const inputBg = isDark ? 'bg-white/[0.04] border-white/[0.08] text-slate-200 placeholder-slate-500' : 'bg-slate-50 border-slate-200 text-slate-800 placeholder-slate-400';
  const contentBg = isDark ? 'bg-white/[0.03]' : 'bg-slate-50';
  const dropdownBg = isDark ? 'bg-[#1e293b] border-white/10' : 'bg-white border-slate-200';
  const dropdownHover = isDark ? 'hover:bg-white/[0.06]' : 'hover:bg-slate-50';
  const tabActive = isDark ? 'bg-sky-500/20 text-sky-400' : 'bg-sky-50 text-sky-600';
  const tabInactive = isDark ? `${textMuted} hover:text-white hover:bg-white/5` : `${textMuted} hover:text-slate-700 hover:bg-slate-100`;
  const paginationBorder = isDark ? 'border-white/5' : 'border-slate-100';
  const closeBtn = isDark ? `${textFaint} hover:text-white hover:bg-white/10` : `${textFaint} hover:text-slate-700 hover:bg-slate-100`;
  const recipientBg = isDark ? 'bg-white/[0.04] border-white/[0.08]' : 'bg-slate-50 border-slate-200';
  const recipientClearBtn = isDark ? `${textFaint} hover:text-white` : `${textFaint} hover:text-slate-700`;

  const [tab, setTab] = useState(initialMode === 'compose' ? 'compose' : 'inbox');
  const [inboxData, setInboxData] = useState({ messages: [], total: 0, page: 1, totalPages: 1 });
  const [sentData, setSentData] = useState({ messages: [], total: 0, page: 1, totalPages: 1 });
  const [selectedMsg, setSelectedMsg] = useState(null);
  const [loading, setLoading] = useState(false);

  // compose
  const [recipient, setRecipient] = useState(initialRecipient);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [composeText, setComposeText] = useState('');
  const [sending, setSending] = useState(false);
  const searchTimer = useRef(null);
  const textareaRef = useRef(null);

  // reset on open
  useEffect(() => {
    if (open) {
      if (initialMode === 'compose') {
        setTab('compose');
        setRecipient(initialRecipient);
        setComposeText('');
        setSearchQuery('');
        setSearchResults([]);
      } else {
        setTab('inbox');
      }
      setSelectedMsg(null);
    }
  }, [open, initialMode, initialRecipient]);

  const fetchInbox = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const res = await axios.get(`/api/message/inbox?page=${page}`);
      setInboxData(res.data);
    } catch { /* ignore */ }
    setLoading(false);
  }, []);

  const fetchSent = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const res = await axios.get(`/api/message/sent?page=${page}`);
      setSentData(res.data);
    } catch { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!open) return;
    if (tab === 'inbox') fetchInbox();
    if (tab === 'sent') fetchSent();
  }, [open, tab, fetchInbox, fetchSent]);

  const openDetail = async (id) => {
    setLoading(true);
    try {
      const res = await axios.get(`/api/message/${id}`);
      setSelectedMsg(res.data);
      // refresh inbox to update read status
      if (tab === 'inbox') fetchInbox(inboxData.page);
    } catch (err) {
      alert(err.response?.data?.errorMessage || '메시지를 불러올 수 없습니다.');
    }
    setLoading(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('이 메시지를 삭제하시겠습니까?')) return;
    try {
      await axios.delete(`/api/message/${id}`);
      setSelectedMsg(null);
      if (tab === 'inbox') fetchInbox(inboxData.page);
      else fetchSent(sentData.page);
    } catch (err) {
      alert(err.response?.data?.errorMessage || '삭제에 실패했습니다.');
    }
  };

  const handleReply = (msg) => {
    setTab('compose');
    setRecipient({
      userType: msg.sender.userType,
      userId: msg.sender.id,
      name: msg.sender.nickName || msg.sender.name,
      profileImage: msg.sender.profileImage,
      adminYn: msg.sender.adminYn,
    });
    setComposeText('');
    setSelectedMsg(null);
  };

  // user search
  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (!searchQuery.trim()) { setSearchResults([]); return; }
    searchTimer.current = setTimeout(async () => {
      setSearching(true);
      try {
        const isPaper = window.location.hostname === 'pinkpaper.kr' || window.location.pathname.startsWith('/paper');
        const res = await axios.get(`/api/message/user-search?q=${encodeURIComponent(searchQuery)}${isPaper ? '&domain=paper' : ''}`);
        setSearchResults(res.data);
      } catch { /* ignore */ }
      setSearching(false);
    }, 300);
    return () => { if (searchTimer.current) clearTimeout(searchTimer.current); };
  }, [searchQuery]);

  const handleSend = async () => {
    if (!recipient) return alert('받는 사람을 선택해주세요.');
    if (!composeText.trim()) return alert('내용을 입력해주세요.');
    setSending(true);
    try {
      await axios.post('/api/message', {
        receiverType: recipient.userType,
        receiverId: recipient.userId,
        content: composeText.trim(),
      });
      alert('메시지를 보냈습니다.');
      setComposeText('');
      setRecipient(null);
      setTab('sent');
      fetchSent();
      onSent?.();
    } catch (err) {
      alert(err.response?.data?.errorMessage || '전송에 실패했습니다.');
    }
    setSending(false);
  };

  // ESC close
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  if (!open) return null;

  const formatDate = (d) => {
    const date = new Date(d);
    const now = new Date();
    const diff = now - date;
    if (diff < 60000) return '방금 전';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}분 전`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}시간 전`;
    return date.toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' });
  };

  const tabBtn = (key, label, icon) => (
    <button
      key={key}
      onClick={() => { setTab(key); setSelectedMsg(null); }}
      className={`flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-lg transition-colors ${
        tab === key ? tabActive : tabInactive
      }`}>
      {icon}
      {label}
    </button>
  );

  const Pagination = ({ data, onPage }) => data.totalPages > 1 && (
    <div className={`flex items-center justify-center gap-2 pt-3 border-t ${paginationBorder}`}>
      <button disabled={data.page <= 1} onClick={() => onPage(data.page - 1)}
        className={`p-1 rounded ${textMuted} hover:${textPrimary} disabled:opacity-30`}><ChevronLeft size={16} /></button>
      <span className={`text-xs ${textFaint}`}>{data.page} / {data.totalPages}</span>
      <button disabled={data.page >= data.totalPages} onClick={() => onPage(data.page + 1)}
        className={`p-1 rounded ${textMuted} hover:${textPrimary} disabled:opacity-30`}><ChevronRight size={16} /></button>
    </div>
  );

  const MessageRow = ({ msg, isInbox }) => {
    const user = isInbox ? msg.sender : msg.receiver;
    return (
      <button
        onClick={() => openDetail(msg.id)}
        className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors rounded-lg ${
          isInbox && !msg.isRead ? unreadRowBg : rowHover
        }`}>
        <UserAvatar
          name={user?.name || '?'}
          profileImage={user?.profileImage}
          userType={user?.userType || 'member'}
          adminYn={user?.adminYn || 'N'}
          size={32}
          showRole={true}
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className={`text-sm truncate ${isInbox && !msg.isRead ? `font-semibold ${textPrimary}` : textSecondary}`}>
              {user?.nickName || user?.name || '알 수 없음'}
            </span>
            <span className={`text-[10px] ${textFaint} shrink-0`}>{formatDate(msg.createdAt)}</span>
          </div>
          <p className={`text-xs ${textMuted} truncate mt-0.5`}>{msg.content}</p>
        </div>
        {isInbox && !msg.isRead && (
          <div className="w-2 h-2 rounded-full bg-sky-400 shrink-0" />
        )}
      </button>
    );
  };

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className={`relative w-full max-w-lg max-h-[80vh] ${modalBg} border ${modalBorder} rounded-2xl shadow-2xl flex flex-col overflow-hidden`}>
        {/* Header */}
        <div className={`flex items-center justify-between px-5 py-3.5 border-b ${headerBorder}`}>
          {selectedMsg ? (
            <button onClick={() => setSelectedMsg(null)} className={`flex items-center gap-1.5 text-sm ${textMuted} hover:${textPrimary} transition-colors`}>
              <ArrowLeft size={16} /> 목록
            </button>
          ) : (
            <div className="flex items-center gap-1">
              {tabBtn('inbox', '받은 메시지', <Inbox size={13} />)}
              {tabBtn('sent', '보낸 메시지', <Send size={13} />)}
              {tabBtn('compose', '쓰기', <Mail size={13} />)}
            </div>
          )}
          <button onClick={onClose} className={`p-1.5 rounded-md ${closeBtn} transition-colors`}>
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading && !selectedMsg && (
            <div className="flex justify-center py-8">
              <div className="w-5 h-5 border-2 border-sky-400/30 border-t-sky-400 rounded-full animate-spin" />
            </div>
          )}

          {/* Detail view */}
          {selectedMsg && (
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <UserAvatar
                  name={selectedMsg.sender?.name || '?'}
                  profileImage={selectedMsg.sender?.profileImage}
                  userType={selectedMsg.sender?.userType || 'member'}
                  adminYn={selectedMsg.sender?.adminYn || 'N'}
                  size={36}
                  showRole={true}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={`text-sm font-semibold ${textPrimary}`}>
                      {selectedMsg.sender?.nickName || selectedMsg.sender?.name}
                    </span>
                    <span className={`text-[11px] ${textFaint}`}>
                      → {selectedMsg.receiver?.nickName || selectedMsg.receiver?.name}
                    </span>
                  </div>
                  <span className={`text-[11px] ${textFaint}`}>
                    {new Date(selectedMsg.createdAt).toLocaleString('ko-KR')}
                  </span>
                </div>
              </div>
              <div className={`${contentBg} rounded-xl p-4 text-sm ${textSecondary} whitespace-pre-wrap leading-relaxed`}>
                {selectedMsg.content}
              </div>
              <div className="flex items-center gap-2 pt-2">
                {selectedMsg.sender?.userType !== 'system' && (
                  <button
                    onClick={() => handleReply(selectedMsg)}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-sky-500/15 text-sky-400 text-xs font-semibold rounded-lg hover:bg-sky-500/25 transition-colors">
                    <Send size={12} /> 답장
                  </button>
                )}
                <button
                  onClick={() => handleDelete(selectedMsg.id)}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-rose-500/15 text-rose-400 text-xs font-semibold rounded-lg hover:bg-rose-500/25 transition-colors">
                  <Trash2 size={12} /> 삭제
                </button>
              </div>
            </div>
          )}

          {/* Inbox list */}
          {!selectedMsg && !loading && tab === 'inbox' && (
            <div className="space-y-1">
              {inboxData.messages.length === 0 ? (
                <div className={`flex flex-col items-center gap-2 py-12 ${textFaint}`}>
                  <Inbox size={28} />
                  <p className="text-sm">받은 메시지가 없습니다.</p>
                </div>
              ) : (
                inboxData.messages.map((msg) => <MessageRow key={msg.id} msg={msg} isInbox={true} />)
              )}
              <Pagination data={inboxData} onPage={(p) => fetchInbox(p)} />
            </div>
          )}

          {/* Sent list */}
          {!selectedMsg && !loading && tab === 'sent' && (
            <div className="space-y-1">
              {sentData.messages.length === 0 ? (
                <div className={`flex flex-col items-center gap-2 py-12 ${textFaint}`}>
                  <Send size={28} />
                  <p className="text-sm">보낸 메시지가 없습니다.</p>
                </div>
              ) : (
                sentData.messages.map((msg) => <MessageRow key={msg.id} msg={msg} isInbox={false} />)
              )}
              <Pagination data={sentData} onPage={(p) => fetchSent(p)} />
            </div>
          )}

          {/* Compose */}
          {!selectedMsg && tab === 'compose' && (
            <div className="space-y-4">
              {/* Recipient */}
              <div className="space-y-1.5">
                <label className={`text-xs font-semibold ${textMuted} uppercase tracking-wide`}>받는 사람</label>
                {recipient ? (
                  <div className={`flex items-center gap-2 px-3 py-2 ${recipientBg} rounded-xl border`}>
                    <UserAvatar
                      name={recipient.name}
                      profileImage={recipient.profileImage}
                      userType={recipient.userType}
                      adminYn={recipient.adminYn || 'N'}
                      size={24}
                      showRole={true}
                    />
                    <span className={`text-sm ${textPrimary} flex-1`}>{recipient.name}</span>
                    <button onClick={() => setRecipient(null)} className={recipientClearBtn}>
                      <X size={14} />
                    </button>
                  </div>
                ) : (
                  <div className="relative">
                    <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                      <Search size={14} className={textFaint} />
                    </div>
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="이름 또는 닉네임으로 검색"
                      className={`w-full pl-9 pr-3 py-2.5 ${inputBg} border rounded-xl text-sm outline-none focus:ring-2 focus:ring-sky-500/30 transition`}
                    />
                    {(searchResults.length > 0 || searching) && (
                      <div className={`absolute top-full left-0 right-0 mt-1 ${dropdownBg} border rounded-xl shadow-xl max-h-48 overflow-y-auto z-10`}>
                        {searching ? (
                          <div className={`px-4 py-3 text-xs ${textFaint}`}>검색 중...</div>
                        ) : (
                          searchResults.map((user) => (
                            <button
                              key={`${user.userType}_${user.id}`}
                              onClick={() => {
                                setRecipient({
                                  userType: user.userType,
                                  userId: user.id,
                                  name: user.nickName || user.name,
                                  profileImage: user.profileImage,
                                  adminYn: user.adminYn,
                                });
                                setSearchQuery('');
                                setSearchResults([]);
                              }}
                              className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-left ${dropdownHover} transition-colors`}>
                              <UserAvatar
                                name={user.name}
                                profileImage={user.profileImage}
                                userType={user.userType}
                                adminYn={user.adminYn || 'N'}
                                size={24}
                                showRole={true}
                              />
                              <div className="min-w-0">
                                <span className={`text-sm ${textPrimary} truncate block`}>{user.nickName || user.name}</span>
                                {user.nickName && user.nickName !== user.name && (
                                  <span className={`text-[10px] ${textFaint}`}>{user.name}</span>
                                )}
                              </div>
                              <span className={`ml-auto text-[10px] px-1.5 py-0.5 rounded-full ${
                                user.userType === 'client' ? 'bg-rose-500/15 text-rose-400' : 'bg-emerald-500/15 text-emerald-400'
                              }`}>
                                {user.userType === 'client' ? '권리사' : '회원'}
                              </span>
                            </button>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="space-y-1.5">
                <label className={`text-xs font-semibold ${textMuted} uppercase tracking-wide`}>내용</label>
                <textarea
                  ref={textareaRef}
                  value={composeText}
                  onChange={(e) => setComposeText(e.target.value)}
                  placeholder="메시지를 입력하세요"
                  rows={5}
                  className={`w-full px-4 py-3 ${inputBg} border rounded-xl text-sm outline-none focus:ring-2 focus:ring-sky-500/30 transition resize-none`}
                />
              </div>

              {/* Send button */}
              <button
                onClick={handleSend}
                disabled={sending || !recipient || !composeText.trim()}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-sky-500/20 hover:bg-sky-500/30 disabled:opacity-40 disabled:cursor-not-allowed text-sky-400 text-sm font-semibold rounded-xl transition-colors">
                <Send size={14} />
                {sending ? '전송 중...' : '보내기'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

export default MessageModal;
